/**
 * useGooglePlacesAutocomplete Hook
 *
 * Provides Google Places Autocomplete functionality using the NEW API (2025)
 * - Uses AutocompleteSuggestion.fetchAutocompleteSuggestions() instead of legacy AutocompleteService
 * - Uses Place.fetchFields() instead of legacy PlacesService
 * - Promise-based async/await pattern
 * - Session token management for cost optimization
 * - Debounced search
 *
 * @see https://developers.google.com/maps/documentation/javascript/place-autocomplete-data
 * @see https://developers.google.com/maps/documentation/javascript/legacy/places-migration-autocomplete
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { importPlacesLibrary } from "@/lib/google-maps";
import type { AddressComponents, PlacePrediction } from "@/types/address";
import { parseGoogleAddressComponents } from "@/utils/addressParser";

interface UseGooglePlacesAutocompleteOptions {
  /**
   * Debounce delay in milliseconds for search requests
   * @default 200
   */
  debounceMs?: number;

  /**
   * Minimum input length before searching
   * @default 2
   */
  minInputLength?: number;

  /**
   * Country restriction (ISO 3166-1 alpha-2 code)
   * @default "mx"
   */
  countryRestriction?: string;

  /**
   * Whether to include all place types or restrict to addresses only
   * @default false (no restrictions - better results like Vercel)
   */
  restrictToAddresses?: boolean;
}

interface UseGooglePlacesAutocompleteReturn {
  /**
   * Whether the Google Places API is ready to use
   */
  isReady: boolean;

  /**
   * Whether a search is in progress
   */
  isLoading: boolean;

  /**
   * Current predictions from autocomplete
   */
  predictions: PlacePrediction[];

  /**
   * Error message if any operation failed
   */
  error: string | null;

  /**
   * Search for address predictions
   */
  search: (input: string) => void;

  /**
   * Get detailed place information and parse address components
   */
  getPlaceDetails: (placeId: string) => Promise<AddressComponents | null>;

  /**
   * Clear current predictions
   */
  clearPredictions: () => void;

  /**
   * Reset session token (call after selecting a place)
   */
  resetSession: () => void;
}

// Places library type from Google Maps
type PlacesLibrary = google.maps.PlacesLibrary;

/**
 * Hook for Google Places Autocomplete functionality (NEW API 2025)
 *
 * @param options - Configuration options
 * @returns Object with autocomplete state and methods
 *
 * @example
 * const { isReady, predictions, search, getPlaceDetails } = useGooglePlacesAutocomplete();
 *
 * // Search for addresses
 * search("Av. Insurgentes");
 *
 * // Get details when user selects a prediction
 * const addressData = await getPlaceDetails(prediction.place_id);
 */
export const useGooglePlacesAutocomplete = (
  options: UseGooglePlacesAutocompleteOptions = {},
): UseGooglePlacesAutocompleteReturn => {
  const {
    debounceMs = 200,
    minInputLength = 2,
    countryRestriction = "mx",
    restrictToAddresses = false,
  } = options;

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Refs for Google services and session management
  const placesLibraryRef = useRef<PlacesLibrary | null>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize Google Places API (New)
  useEffect(() => {
    let isMounted = true;

    const initializeServices = async () => {
      try {
        // Import the places library using v2 functional API
        const placesLib = await importPlacesLibrary();

        if (!isMounted) return;

        placesLibraryRef.current = placesLib;

        // Create initial session token
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken();

        setIsReady(true);
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        const message =
          err instanceof Error ? err.message : "Failed to load Google Places";
        setError(message);
        console.error("Google Places initialization error:", err);
      }
    };

    initializeServices();

    return () => {
      isMounted = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Reset session token (should be called after selecting a place)
   */
  const resetSession = useCallback(() => {
    if (typeof google !== "undefined" && google.maps?.places) {
      sessionTokenRef.current =
        new google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  /**
   * Clear predictions
   */
  const clearPredictions = useCallback(() => {
    setPredictions([]);
  }, []);

  /**
   * Search for address predictions using the NEW API
   * Uses AutocompleteSuggestion.fetchAutocompleteSuggestions()
   */
  const search = useCallback(
    (input: string) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Validate input
      if (!input || input.length < minInputLength) {
        setPredictions([]);
        setIsLoading(false);
        return;
      }

      // Check if service is ready
      if (!placesLibraryRef.current || !isReady) {
        return;
      }

      setIsLoading(true);

      // Debounce the search
      debounceTimerRef.current = setTimeout(async () => {
        try {
          // Build request for the NEW API
          // Note: NOT restricting types gives much better results (like Vercel's implementation)
          const request: google.maps.places.AutocompleteRequest = {
            input,
            includedRegionCodes: [countryRestriction],
            sessionToken: sessionTokenRef.current || undefined,
            // Only add type restrictions if explicitly requested
            ...(restrictToAddresses && {
              includedPrimaryTypes: ["street_address", "route", "premise", "subpremise", "locality"],
            }),
          };

          // Use the NEW AutocompleteSuggestion.fetchAutocompleteSuggestions() API
          const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request,
            );

          if (suggestions && suggestions.length > 0) {
            // Map to our PlacePrediction interface
            const mappedPredictions: PlacePrediction[] = suggestions
              .filter((s) => s.placePrediction) // Ensure placePrediction exists
              .map((suggestion) => {
                const pred = suggestion.placePrediction!;
                return {
                  place_id: pred.placeId,
                  description: pred.text?.text || "",
                  structured_formatting: {
                    main_text: pred.mainText?.text || "",
                    secondary_text: pred.secondaryText?.text || "",
                  },
                };
              });

            setPredictions(mappedPredictions);
            setError(null);
          } else {
            setPredictions([]);
            setError(null);
          }
        } catch (err) {
          console.error("Autocomplete search error:", err);
          setPredictions([]);

          // Only set error for actual errors, not empty results
          if (err instanceof Error) {
            // Ignore "ZERO_RESULTS" type errors
            if (!err.message.includes("ZERO_RESULTS")) {
              setError(err.message);
            }
          }
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [isReady, minInputLength, countryRestriction, debounceMs, restrictToAddresses],
  );

  /**
   * Get place details using the NEW API
   * Uses Place.fetchFields() instead of PlacesService.getDetails()
   */
  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<AddressComponents | null> => {
      if (!placesLibraryRef.current || !isReady) {
        return null;
      }

      try {
        // Create a Place instance with the place ID
        const { Place } = placesLibraryRef.current;
        const place = new Place({
          id: placeId,
        });

        // Fetch the fields we need using the NEW API
        await place.fetchFields({
          fields: ["addressComponents", "formattedAddress", "location"],
        });

        // Reset session token after getting details (session complete)
        resetSession();

        if (place.addressComponents) {
          // Convert the NEW AddressComponent format (longText/shortText)
          // to the legacy format (long_name/short_name) for our parser
          const legacyComponents = place.addressComponents.map((component) => ({
            long_name: component.longText || "",
            short_name: component.shortText || "",
            types: component.types || [],
          }));

          const parsed = parseGoogleAddressComponents(
            legacyComponents,
            place.formattedAddress || "",
          );

          return parsed;
        }

        return null;
      } catch (err) {
        console.error("Failed to get place details:", err);
        resetSession();
        return null;
      }
    },
    [isReady, resetSession],
  );

  return {
    isReady,
    isLoading,
    predictions,
    error,
    search,
    getPlaceDetails,
    clearPredictions,
    resetSession,
  };
};
